import React, { useState } from 'react';
import CustomButton from '../AddButton';
import { Plus } from '../../common/svg';

type TabType = {
  label: string;
  key: string;
  count?: number;
  icon?: React.ReactNode;
};

type CustomTabSwitcherProps<T> = {
  tabs: TabType[];
  data: Record<string, T[]>;
  renderMap: {
    [tabKey: string]: (items: T[]) => React.ReactNode; // Changed to receive the entire array
  };
  showSearch?: boolean;
  showAddButton?: boolean;
  onAddClick?: () => void;
  onSearch?: (query: string) => void;
};

export function CustomTabSwitcher<T>({
  tabs,
  data,
  renderMap,
  showSearch = true,
  showAddButton = true,
  onAddClick,
  onSearch,
}: CustomTabSwitcherProps<T>) {
  const [activeTab, setActiveTab] = useState(tabs[0]?.key || '');
  const [searchQuery, setSearchQuery] = useState('');
  
  const handleSearch = (value: string) => {
    setSearchQuery(value);
    if (onSearch) onSearch(value);
  };
  
  const items = data[activeTab] || [];
  
  return (
    <div className="w-full p-4 bg-white">
      {/* Header with tabs on left and actions on right */}
      <div className="flex justify-between items-center border-b-2 border-gray-300 border-dashed pb-2">
        {/* Tabs on left */}
        <div className="flex">
          {tabs.map((tab) => (
            <button
              key={tab.key}
              className={`px-4 py-2 font-[700] flex items-center gap-1 ${
                activeTab === tab.key
                  ? 'border-b-2 border-[#444FBD] text-[#444FBD]'
                  : 'text-[#444FBD]'
              }`}
              onClick={() => setActiveTab(tab.key)}
            >
              {tab.icon && <span>{tab.icon}</span>}
              <span className='text-[14px]'>{tab.label}</span>
              {typeof tab.count === 'number' && (
                <span className="ml-1 text-xs bg-gray-100 rounded px-1.5 py-0.5">
                  {String(tab.count).padStart(2, '0')}
                </span>
              )}
            </button>
          ))}
        </div>
        
        {/* Search and Add button on right */}
        <div className="flex items-center gap-2 mr-2">
          {showSearch && (
            <div className="relative flex items-center">
              <svg className="w-4 h-4 text-gray-400 absolute left-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search"
                className="border rounded-md pl-8 pr-3 py-1 text-sm w-44"
              />
            </div>
          )}
          {showAddButton && (
            <CustomButton label="" onClick={onAddClick} icon={Plus} />
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="pt-4 ">
        {items.length > 0 ? (
          <div className="w-full">
            {/* Render the component based on the active tab, passing the entire items array */}
            {renderMap[activeTab] ? renderMap[activeTab](items) : null}
          </div>
        ) : (
          <p className="text-gray-400 text-sm">No data available.</p>
        )}
      </div>
    </div>
  );
}